import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { withTranslation } from 'react-i18next';
import { FileInput, Toaster, ProgressBar, Classes, Intent, Switch, TextArea, Button, Card, Checkbox, FormGroup, InputGroup, H3, H4, H6 } from '@blueprintjs/core';

import CorpusHeader from '../common/CorpusHeader';
import CorpusCard from './CorpusCard';
import SelectPartKeys from './SelectPartKeys';
import EnhancedSingleSelect from '../../../common/EnhancedSingleSelect';
import { Dialog } from '@blueprintjs/core';
import { CORPUS_TYPES } from '../constants.js';
import Cookies from 'js-cookie';
import { FRONT_USER_FRONT_LOGGED_IN_COOKIE_NAME, FRONT_USER_LANG_COOKIE_NAME } from '../../../auth/constants';
import { PUBLIC_URL, SERVER_URL } from '../../../constants';

const emptyCorpus = {
  id: 0,  // the value of `id` is given by currentCorpusId
  ready: false,
  name: '',
  keywords: '',
  collection: '',
  categories: [],
  authors: [],
  titles: [],
  sourceLangs: [],
  period: [],
  otherAttr:'',
  otherAttrValues: []
};

class CustomCorpus extends React.PureComponent {

  state = {
    // Options available to the user
    // global params
    allLangs: ["af","ar","be","bg","bxr","ca","cop","cs","cu","da","de","el","en","es","et","eu","fa","fi","fr","frm","fro","ga","gd","gl","got","grc","he","hi","hr","hsb","hu","hy","id","it","ja","kk","kmr","ko","la","lt","lv","mr","mt","nl","nn","no","olo","orv","pl","pt","ro","ru","sk","sl","sme","sr","sv","swl","ta","te","tr","ug","uk","ur","vi","wo","zh"],
    alignedLangs: [],
    // subcorpus
    collsDictMatchingGlobalParams: {},  // the matching collections
    collPartKeys: [],

    // Choices made by the user
    // global params
    name: '',
    lang: '',
    modelName:'',
    alignedLang: '',  // optional
    l1:'',
    l2:'',
    keywords:'',
    parallelCorpus:false,
    alreadyAligned:false,
    alreadyParsed:false,
    markDD:false,
    xpathQuery4TagIndex:'',

    // subCorpora and partition keys
    currentCorpusId: 0,  // a newly created corpus takes currentCorpusId + 1 as id
    subCorpora: [],
    partKeys: "",  // optional

    // presentational
    globalParamsReady: false,
    isPartToggled: false,
    isOpenModal:false,

    ready:false, // indicates whether corpus is ready

    importTexts:false, // displays the import corpus form
    importUsingUpload:true, // display the upload input field
    importProcessStage:0, // 0: before, 1: import process is running, 2: import process is done
    selectedFile:null,
    textContent:"",
  }

  refHandlers = {
    toaster: (ref) => this.toaster = ref,
  };

  // if the current corpus is valid, it is reformatted to fit the server format
  validateCorpusData =() => {
    const { name, keywords, lang, alignedLang, subCorpora, partKeys } = this.state;

    // check corpus is valid
    if (! name || ! lang || subCorpora.length < 1) {
      // comprehensive visual feedback
      if (! name) {
        // red border -> intent danger
        this.toaster.show({
          message: this.props.t('corpusNameIsNotDefined'),
          intent: 'danger',
          icon: 'warning-sign',
        });
      }
      if (! lang) {
        // btn with intent danger
        this.toaster.show({
          message: this.props.t('languageIsNotDefined'),
          intent: 'danger',
          icon: 'warning-sign',
        });

      }
      if (subCorpora.length < 1) {
        // msg "Please add at least one corpus"
        // ideally, also make the "Add this corpus to the search area" blinck
        this.toaster.show({
          message: this.props.t('pleaseAddAtLeastOneSubcorpus'),
          intent: 'danger',
          icon: 'warning-sign',
        });
      }
      this.setState({'ready':false});
      return {};
    }
    this.setState({'ready':true});

    // remove unnecessary information from corpus items, namely `id` and `ready`,
    // and transform the `period` key into `minYear` and `maxYear`.
    const cleanSubCorpora = subCorpora.map( (corpus) => {
      if (corpus.alias) {
        return {
          name: corpus.name,
          collection: [corpus.collection],
          constraints: {},
          alias: corpus.alias,
          nb_toks: corpus.nbToks
        }
      } else {
        return {
          name: corpus.name,
          collection: [corpus.collection],
          constraints: {
            categories: corpus.categories,
            authors: corpus.authors,
            titles: corpus.titles,
            sourceLangs: corpus.sourceLangs,
            [corpus.otherAttr]:corpus.otherAttrValues

          },
          year_min: corpus.period? corpus.period[0]:null,
          year_max: corpus.period? corpus.period[1]:null,
          nb_toks: corpus.nbToks
        }
      }
    });

    // rename `sourceLangs` into `source_languages` to fit the API expectation
    const customSubCorpora = cleanSubCorpora.map(subCorpus => {
      const sourceLangs = subCorpus.constraints.sourceLangs;
      delete subCorpus.constraints.sourceLangs;
      subCorpus.constraints.source_languages = sourceLangs;
      return subCorpus;
    });

    const customCorpus = {
      type: CORPUS_TYPES.CUSTOM,
      name: name,
      language: lang,
      keywords: keywords,
      partition: partKeys,
      aligned_language: alignedLang,
      subCorpora: customSubCorpora,
    };

    return customCorpus ;
  }

  onCustomCorpusReady = () => {
    const customCorpus=this.validateCorpusData();
    if (customCorpus) {
      // corpusReady callback wants a list of names
      customCorpus.name=[customCorpus.name];
      this.props.corpusReadyCallback(customCorpus);
    }
  }

  onChangeLang = (e) => {
    const newLang = e.target.value;

    // get the available aligned languages
    const alignedLangs = new Set();  // use a set to ensure no duplicates
    if (this.props.collections[newLang]) {
      const collsInSelectedLang = this.props.collections[newLang];
      for (const coll of Object.values(collsInSelectedLang)) {
        if (coll.alignedLanguages) {
          for (const alignedLang of coll.alignedLanguages) {
            alignedLangs.add(alignedLang); 
          }
        }
      }

      this.setState({
        lang: newLang,
        alignedLangs: [...alignedLangs],
        alignedLang: '',
      });
    } else {
      this.setState({
        lang: newLang,
      });
    }
  }

  onChangeAlignedLang = (e) => {
    const newAlignedLang = e.target.value;
    this.setState({ alignedLang: newAlignedLang });
  }

  onGlobalParamsReadyChange = () => {
    const newGlobalParamsReady = !this.state.globalParamsReady;

    if (newGlobalParamsReady) {
      this.setGlobalParams(newGlobalParamsReady);
      this.createNewCorpus();
    } else {
      //this.resetGlobalParams(newGlobalParamsReady);
    }
  }

  setGlobalParams = (newGlobalParamsReady) => {
    // prepare the collections matching the language and the aligned language
    const collsDictMatchingGlobalParams = this.props.collections[this.state.lang];

    if (this.state.alignedLang) {
      for (const collName in collsDictMatchingGlobalParams) {
        const coll = collsDictMatchingGlobalParams[collName];
        if (! coll.alignedLanguages || !coll.alignedLanguages.includes(this.state.alignedLang)) {
          delete collsDictMatchingGlobalParams.collName;
        }
      }
    }

    this.setState({
      globalParamsReady: newGlobalParamsReady,
      collsDictMatchingGlobalParams: collsDictMatchingGlobalParams,
      //collsMatchingGlobalParams: Object.keys(collsDictMatchingGlobalParams),
    });
  }

  resetGlobalParams = () => {
    // basically reset all choices made by the user
    this.setState({
      globalParamsReady: false,
      name: '',
      keywords:'',
      lang: '',
      alignedLang: '',
      modelName:'',
      l1:'',
      l2:'',
      parallelCorpus:false,
      alreadyAligned:false,
      currentCorpusId: 0,  
      subCorpora: [],
      partKeys: "",
      collsDictMatchingGlobalParams: {},
      alignedLangs: [],
      isPartToggled: false,
      collPartKeys: [],
      isOpenModal:false,
      ready:false,
      importTexts:false,
      importProcessStage:0,
      selectedFile:null,
      textContent:"",
    });
  }

  // Called by the "Add a subcorpus" button
  createNewCorpus = () => {
    const newCorpusId = this.state.currentCorpusId + 1;
    const newCorpus = { ...emptyCorpus, id: newCorpusId, name:'corpus_'+newCorpusId };
    this.setState(prevState => ({
      subCorpora: [...prevState.subCorpora, newCorpus],
      currentCorpusId: newCorpusId,
    }));
  }

  // array.map allows some neat things: https://stackoverflow.com/a/37585362
  updateCorpus = (newCorpus) => {
    const subCorpora = this.state.subCorpora;
    const newSubCorpora = subCorpora.map(corpus => (corpus.id === newCorpus.id ? newCorpus : corpus));
    //console.log("updating",newSubCorpora);
    this.setState({ subCorpora: newSubCorpora });
  }

  deleteCorpus = (corpusToDel) => {
    const subCorpora = [...this.state.subCorpora];  // not to mutate the state directly
    const newSubCorpora = subCorpora.filter(corpus => corpus.id !== corpusToDel.id);
    this.setState({ subCorpora: newSubCorpora, isPartToggled: false, partKeys: null });
  }

  onPartToggle = () => {
    // get the partition keys of the collection
    const subCorpora = this.state.subCorpora;
    const collections = this.props.collections;
    const collPartKeys = collections['collection'][subCorpora[0].collection].partition;

    this.setState(prevState => {
      const newIsPartToggled = !prevState.isPartToggled;
      return { isPartToggled: newIsPartToggled, collPartKeys: collPartKeys };
    });
  }

  handlePartReady = (flatPartKeys) => {
    this.setState({ partKeys: flatPartKeys });
  }

  handlePartDelete = () => {
    this.setState({ partKeys: "", isPartToggled: false, collPartKeys: [] });
  }
  handleOnCorpusSave =() => {
    this.toggleModal();
  }

  handleOnConfirmCorpusSave =() => {
    this.toggleModal();
    const endpoint = '/save_user_data.ajax.php';
    
    const customCorpus=this.validateCorpusData();
    if (! customCorpus) {
      return;
    }

    const data = {
      type:'corpus',
      name:this.state.name,
      content:customCorpus
    };


    fetch(
      process.env.REACT_APP_API_BASE + endpoint, {
        credentials: process.env.REACT_APP_CREDENTIALS,
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
        body: JSON.stringify(data),
      })
      .then((res) => {
        if (res.status !== 200) {
          console.log(`Issue reaching ${endpoint}`);
          return;
        }
        res.json().then((data) => {
          this.toaster.show({
            message: this.props.t('corpusHasBeenSaved'),
            intent: 'success',
            icon: 'tick',
          });
          this.setState({corpusIsSaved:true})
          this.props.refreshSavedData();
        })
      })
      .catch(() => {
        console.log(`Network error when trying to fetch ${process.env.REACT_APP_API_BASE + endpoint}`);
      })
  }

  toggleModal = () => {
    this.setState(prevState => ({ isOpenModal: !prevState.isOpenModal }));
  }

  importCard = () => {
    this.setState({ importTexts:true })
  };

  inputFileChangeHandler= (event) => {
    this.setState({importReady:true,selectedFile:event.target.files[0]});
	};

  switchUseUploadHandler = () => {
    this.setState({ importUsingUpload : ! this.state.importUsingUpload });
  }

  uploadFile = () => {
    const formData = new FormData();
    const partKey = this.state.partKeys ? this.state.partKeys : "sourcefilename";
    console.log(partKey);
    // preparing data
    formData.append('name',this.state.name);
    formData.append('keywords',this.state.keywords);
    formData.append('extractPatterns',this.props.importParamValues.extractPatterns);
    formData.append('extractLexicometry',this.props.importParamValues.extractLexicometry);
    formData.append('idSentPattern',this.props.importParamValues.idSentPattern);
    formData.append('timeIntPattern',this.props.importParamValues.timeIntPattern);
    formData.append('speakerPattern',this.props.importParamValues.speakerPattern);
    formData.append('maxSpanSize',this.props.importParamValues.maxSpanSize);
    formData.append('TTRSpanSize',this.props.importParamValues.TTRSpanSize);
    formData.append('useLemma4Repet',this.props.importParamValues.useLemma4Repet);
    formData.append('supprUnderScore',this.props.importParamValues.supprUnderScore);
    formData.append('modelName',this.state.modelName);
    formData.append('xpathQuery4TagIndex',this.state.xpathQuery4TagIndex);    
    formData.append('partKey',partKey);
    formData.append('lang',this.state.lang);
    formData.append('l1',this.state.l1);
    formData.append('l2',this.state.l2);
    formData.append('alreadyAligned',this.state.alreadyAligned);
    formData.append('alreadyParsed',this.state.alreadyParsed);
    formData.append('markDD',this.state.markDD);


    if (this.state.importUsingUpload) {
      formData.append('File',this.state.selectedFile);
    } else {
      formData.append('textContent',this.state.textContent);
    }

    const endpoint = '/uploadAndProcessZip.ajax.php';
    this.setState({importProcessStage:1});
    fetch(
      process.env.REACT_APP_API_BASE + endpoint, {
        credentials: process.env.REACT_APP_CREDENTIALS,
        method: 'POST',
        body: formData,
        timeout: 10000000,
      })
    .then((res) => {
        if (res.status !== 200) {
          console.log(`Issue reaching ${endpoint}`);
          this.setState({importProcessStage:0});
          this.toaster.show({
            message: this.props.t('anErrorHasOccuredDuringImportation'),
            intent: 'danger',
            icon: 'warning-sign',
          });
          return;
        }
        res.json().then((data) => {
          if (data.tokenCredit===0) {
            this.setState({importProcessStage:0});
            this.toaster.show({
              message: this.props.t('tokenCreditExhausted'),
              intent: 'danger',
              icon: 'warning-sign',
            });
          } else {
            if (data.corpusAlreadyExists===1) {
              this.setState({importProcessStage:0});
              this.toaster.show({
                message: this.props.t('corpusAlreadyExists'),
                intent: 'danger',
                icon: 'warning-sign',
              });
            } else {
              this.setState({importProcessStage:2});
              this.setState({globalParamsReady:true, corpusIsSaved:true, subCorpora:[{ready:true,collection:data.collection,id:data.name,name:data.name,nbToks:data.nbToks}]});
              this.toaster.show({
                message: this.props.t('corpusHasBeenUploadedImportationStateIsDisplayedOnCorpusList'),
                intent: 'success',
                icon: 'tick',
              });
              this.props.refreshSavedData();
            }
          }
        })
      })
    .catch(() => {
        // when corpus is big, the query is often broken by timeout
        // let(s check now if the corpus is ready
        const endpoint = '/users/'+Cookies.get(FRONT_USER_FRONT_LOGGED_IN_COOKIE_NAME)+'/corpus/'+this.state.name+'.json';
        this.fetchJsonCompleted(endpoint,0);
       
        //console.log(`Network error when trying to fetch ${process.env.REACT_APP_API_BASE + endpoint}`);
      }
    );
  }

  // contrôle la présence du json contenant le résultat. Le fetch est exécuté en boucle pendant 10 fois, avec une minute d'attente entre chaque exécution 
  fetchJsonCompleted=(endpoint,depth) => {
    if (depth<10) {
      fetch(
        process.env.REACT_APP_API_BASE + endpoint, {
          credentials: process.env.REACT_APP_CREDENTIALS,
          method: 'GET',
        })
      .then((res) => {
          if (res.status !== 200) {
            console.log(`Issue reaching ${endpoint}`);
            this.setState({importProcessStage:0});
            return;
          }
          res.json().then((data) => {
            if (data.nbToks && data.subCorpora) {
              this.setState({importProcessStage:2});
              this.setState({globalParamsReady:true, corpusIsSaved:true, subCorpora:[{ready:true,collection:data.subCorpora[0].collection,id:data.name,name:data.name,nbToks:data.nbToks}]});
              this.toaster.show({
                message: this.props.t('corpusHasBeenSaved'),
                intent: 'success',
                icon: 'tick',
              });
              return 
            } else {
              // attend une minute avant de relancer le fetch
              setTimeout(
                () => this.fetchJsonCompleted(endpoint,depth+1),
                60000
              );
            }
          }
          );
        })
    } else {
      this.toaster.show({
        message: this.props.t('corpusProcessingIsLong'),
        intent: 'danger',
        icon: 'warning-sign',
      });
    }
  }


  render() {
    const { t } = this.props;
    const { isOpenModal, importUsingUpload } = this.state;
    const langFormat = Cookies.get(FRONT_USER_LANG_COOKIE_NAME)==="en"? "en-EN":"fr-FR";
    const userFrontLoggedIn = Cookies.get(FRONT_USER_FRONT_LOGGED_IN_COOKIE_NAME);

    const colls=this.state.collsDictMatchingGlobalParams? this.state.collsDictMatchingGlobalParams:this.props.collections;
    const commonCollections = Object
        .keys(colls)
        .map(collName => colls[collName].collection)
        .filter(function(elem, index, self) {
          return index === self.indexOf(elem);
        }).sort();

    // array.reducer allows neat things too!
    const allSubCorporaReady = this.state.subCorpora.reduce(((prev, curr) => (
      !curr.ready ? false : prev
    )), true);

    const corpusCards = this.state.subCorpora.map(corpus => (
      <CorpusCard
        key={corpus.id}
        commonCollections={commonCollections}
        commonCorpora={colls}
        privateCollections={this.props.privateCollections}
        lang={this.state.lang}
        alignedLang={this.state.alignedLang}
        corpus={corpus}
        onCorpusReady={this.updateCorpus}
        onDeleteCorpus={this.deleteCorpus}
      />
    ));

    const corpusRecapList = this.state.subCorpora.map(corpus => {
      if (corpus.ready) {
        if (importUsingUpload) {
          return "";
        } else {
          return <li key={corpus.id}>{corpus.name} : {Intl.NumberFormat(langFormat, { useGrouping: true }).format(corpus.nbToks)} tokens, {corpus.nbDocs} documents</li>;
        }
      }
      return null;
    }).filter( element =>  element !== null );

    const globalParamBoxClasses = classNames(
      'margin-bottom-1-rem',
      'margin-right-05-rem',
      {'active-card': !this.state.globalParamsReady},
      {'success-card': this.state.globalParamsReady},
    );
    
    //console.log("userFrontLoggedIn=",userFrontLoggedIn,"corpusRecapList=",corpusRecapList,"importUsingUpload", importUsingUpload);

    return (
      <>
        <CorpusHeader
          title={t('customCorpusTitle')}
          explanations={t('customCorpusDesc')}
          goToQuery={this.onCustomCorpusReady}
          ready={this.state.ready}
        />

        <div className="flex">
          <div className="flex-two-third">
            
            {/* Global parameters */}
            <Card elevation={2} className={globalParamBoxClasses}>
              <H3 className="margin-bottom-1-5rem">{t('globalParams')}</H3>
              <FormGroup label={t('corpusName')} labelFor="corpusName" inline={true}>
                <InputGroup
                  disabled={this.state.globalParamsReady}
                  id="corpusName"
                  value={this.state.name}
                  onChange={(e) => this.setState({ name: e.target.value })}
                />
              </FormGroup>
              <FormGroup 
                label={t('keywords')} 
                labelFor="keywords" 
                helperText={t('keywordsHelp')}
                inline={true}
              >
                <InputGroup
                  disabled={this.state.globalParamsReady}
                  id="keywords"
                  value={this.state.keywords}
                  onChange={(e) => this.setState({ keywords: e.target.value })}
                />
              </FormGroup>
              <Checkbox
                checked={this.state.parallelCorpus}
                label={t('parallelCorpus')}
                onChange={(e)=>this.setState({parallelCorpus:!(this.state.parallelCorpus)})}
              />

              { ! this.state.parallelCorpus && 
                <>

                  <EnhancedSingleSelect
                    disabled={this.state.globalParamsReady}
                    value={this.state.lang}
                    label={t('corpusLang')}
                    name={"lang"}
                    hasDefault={true}
                    options={this.state.allLangs}
                    onChange={this.onChangeLang}
                  />

                  <H6><u>{t('advancedParams')}</u></H6>
                  <EnhancedSingleSelect
                    disabled={this.state.globalParamsReady}
                    value={this.state.alignedLang}
                    hasDefault={true}
                    hasSelectableDefault={true}
                    label={t('alignedLang')}
                    name={"alignedLang"}
                    options={this.state.alignedLangs}
                    onChange={this.onChangeAlignedLang}
                  />
                </>
              }

              { this.state.parallelCorpus && 
                <>
                  <Checkbox
                    checked={this.state.alreadyAligned}
                    label={t('alreadyAligned')}
                    onChange={(e)=>this.setState({alreadyAligned:!(this.state.alreadyAligned)})}
                  />

                  <EnhancedSingleSelect
                    disabled={this.state.globalParamsReady}
                    value={this.state.l1}
                    label={t('l1')}
                    name={"lang1"}
                    hasDefault={true}
                    options={this.state.allLangs}
                    onChange={(e)=>{ this.setState({l1:e.target.value})}}
                    alignIndicator='right'
                  />

                  <EnhancedSingleSelect
                    disabled={this.state.globalParamsReady}
                    value={this.state.l2}
                    label={t('l2')}
                    name={"lang2"}
                    hasDefault={true}
                    options={this.state.allLangs}
                    onChange={(e)=>{ this.setState({l2:e.target.value})}}
                  />
                </>
              }

              {this.state.name && this.state.lang && ! this.state.importTexts && ! this.state.globalParamsReady &&
                  <Button text={t('selectExistingTexts')} onClick={this.onGlobalParamsReadyChange} />
              }
              &nbsp;&nbsp;
              {this.state.name && (this.state.lang || (this.state.l1 && this.state.l2)) && ! this.state.importTexts && ! this.state.globalParamsReady &&
                  <Button text={t('importYourOwnTexts')} onClick={this.importCard} />
              }
              <Button text={t('reset')} onClick={this.resetGlobalParams} />
            </Card>

            { this.state.importTexts && this.state.importProcessStage < 2 && 
              <Card elevation={2} className={globalParamBoxClasses}>
                <H3 className="margin-bottom-1-5rem">{t('importYourOwnTexts')}</H3>
                { ! this.state.parallelCorpus && 
                  <Switch
                    checked={importUsingUpload}
                    name="importUsingUpload"
                    label={t('importUsingUpload')}
                    onChange={this.switchUseUploadHandler}
                  />
                }
                <FormGroup label={t('partKeys')} labelFor="partKeys" inline={true}>
                  <InputGroup
                    id="partitionKey"
                    defaultValue="sourcefilename"
                    onChange={(e) => this.setState({ partKeys:e.target.value })}
                  />
                </FormGroup>
                <Checkbox
                    checked={this.state.alreadyParsed}
                    label={t('alreadyParsed')}
                    onChange={(e)=>this.setState({alreadyParsed:!(this.state.alreadyParsed)})}
                />
                <Checkbox
                    checked={this.state.markDD}
                    label={t('markDD')}
                    onChange={(e)=>this.setState({markDD:!(this.state.markDD)})}
                />
                { ! this.state.alreadyParsed && 
                  <FormGroup label={t('StanzaModelName')} labelFor="modelName" inline={true}>
                    <InputGroup
                      disabled={this.state.globalParamsReady}
                      id="modelName"
                      value={this.state.modelName}
                      onChange={(e) => this.setState({ modelName: e.target.value })}
                    />
                  </FormGroup>
                }
                { importUsingUpload && 
                  <>
                    <FormGroup label={t('xpathQuery4TagIndex')} labelFor="xpathQuery4TagIndex" inline={true}>
                      <InputGroup
                        disabled={this.state.globalParamsReady}
                        id="xpathQuery4TagIndex"
                        value={this.state.xpathQuery4TagIndex}
                        onChange={(e) => this.setState({ xpathQuery4TagIndex: e.target.value })}
                      />
                    </FormGroup>
                    <p>{t('optionalUseCommaToSeparate')}</p>
                  </>
                }                
                { importUsingUpload &&
                  <>
                    <FormGroup label={t('importZipFile')} inline={true}>
                      <FileInput name='FileName' text={this.state.selectedFile? this.state.selectedFile.name:t('uploadFile')} onInputChange={this.inputFileChangeHandler} />
                    </FormGroup>

                    { ! this.state.parallelCorpus && 
                      <div>
                       {t('formatExplanation1')}
                       {t('formatExamplesCanBeFindHereColon')}<a href={SERVER_URL+PUBLIC_URL+"/doc/examples"} target='blank'>{SERVER_URL+PUBLIC_URL+"/doc/examples"}</a>
                      </div>
                    }
                    { this.state.parallelCorpus && 
                      <div>
                       {t('formatExplanation2')}
                      </div>
                    }
                  </>
                }
                {! importUsingUpload &&
                  <>
                    <TextArea
                      name='FileContent'
                      onChange={(e)=>{this.setState({textContent:e.target.value})}} 
                      style={{'height': '100px', 'width': '600px'}}
                    />
                     <div>
                        <br/>
                        {t('contentExplanation')}
                    </div>
                  </>
                }

                <br/>
               
                { (this.state.importReady || !importUsingUpload) &&
                    <Button text={t('uploadFile')} onClick={this.uploadFile} />
                }
                
                { this.state.importProcessStage===1 &&
                    <>
                      <br/>
                      <p>{t('corpusUploading')}</p>
                      <ProgressBar />
                    </>
                }
              </Card>
            }

            {! this.state.importTexts &&
              corpusCards
            }

            {! this.state.importTexts && this.state.globalParamsReady && allSubCorporaReady &&
              <div className="flex">
                {this.state.subCorpora.length === 1 && !this.state.isPartToggled &&
                  <Button text={t('partSubCorpus')} onClick={this.onPartToggle} />
                }
                {!this.state.isPartToggled &&
                  <Button text={t('addSubCorpus')} onClick={this.createNewCorpus} />
                }
              </div>
            }

            {this.state.isPartToggled &&
              <SelectPartKeys
                collPartKeys={this.state.collPartKeys}
                onPartReady={this.handlePartReady}
                onDeletePart={this.handlePartDelete}
              />
            }

          </div>

          {/* RECAP + SAVE */}
          <div className="flex-one-third">
            <Card elevation={2} className="margin-bottom-1-rem margin-left-05-rem">
              <H4>{t('searchRecapColon')} {this.state.name}</H4>
              <ul>
                {corpusRecapList}
              </ul>
              <center>
                { userFrontLoggedIn && 
                  corpusRecapList.length>0 && 
                  corpusRecapList[0] !== null &&
                  ! this.state.importTexts &&
                  ! this.state.corpusIsSaved &&
                  <Button onClick={this.handleOnCorpusSave}>{t('saveCustomCorpus')}</Button>
                }
              </center>
            </Card>
          </div>

          <Dialog
            isOpen={isOpenModal}
            onClose={this.toggleModal}
            title={t('saveCustomCorpus')+" «"+this.state.name+"»"}
            style={{'width': '400px'}}  // 400px seems a good deal
          >
            <div className={Classes.DIALOG_BODY}>
              {t('currentCorpusCanBeSavedInYourPersonalDirectoriesOnTheServer')}
            </div>
            <div className={Classes.DIALOG_FOOTER}>
              <div className={Classes.DIALOG_FOOTER_ACTIONS}>

                <Button onClick={this.toggleModal}>Close</Button>

                <Button
                    onClick={this.handleOnConfirmCorpusSave}
                    intent={Intent.PRIMARY}
                >
                    {t('confirmSaving')}
                </Button>
              </div>
            </div>

          </Dialog>

        </div>

        {/* Special component, out of the normal flow */}
        <Toaster ref={this.refHandlers.toaster} />
      </>
    );
  }
}

CustomCorpus.propTypes = {
  importParamValues: PropTypes.object,
  corpusReadyCallback: PropTypes.func,
  collections:PropTypes.object, // should be called commonCorpora
  privateCollections: PropTypes.array,
  refreshSavedData: PropTypes.func,  
};

export default withTranslation()(CustomCorpus);
